// ./src/components/features/Memberships/MembershipsFilterBar/MembershipsFilterBar.tsx
//
// Filter controls for the Memberships list: by type, by validity (active /
// expiring soon / expired), by source (internal / external), and — #281 — the
// payments — Real (where it opens) · Test (bought with a test card; admit only
// on test-mode events) · All, the same control as the gifts lists. Fully controlled —
// the page owns the value and maps it to a MembershipFilter.
//
// Responsive: the selects flex and wrap to full width on narrow screens. Type
// options are read from the app-wide MembershipTypesProvider.

import React, { useMemo } from "react";
import {
  Group,
  SegmentedControl,
  Input,
  Tooltip,
  Button,
  Box,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { MembershipValidity, MEMBERSHIP_EXPIRING_SOON_DAYS } from "@/utils/api";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { useAppEnvironment } from "@/contexts";
import { useOptionalMembershipTypesContext } from "@/contexts/MembershipTypesContext/MembershipTypesContext";
import { AppIcon, DEFAULT_PAYMENTS_VIEW, PaymentsView, SelectField, TestPaymentsControl } from "@/components/generic";

// ============================================
// Types
// ============================================

export type MembershipSource = "internal" | "external";

export interface MembershipsFilterValue {
  membershipTypeId: string | null;
  validity: MembershipValidity | null;
  source: MembershipSource | null;
  /** #281 — real (the default), test or all memberships. */
  payments: PaymentsView;
}

export interface MembershipsFilterBarProps {
  /** Current filter selection (controlled). */
  value: MembershipsFilterValue;

  /** Called with the next selection whenever a control changes. */
  onChange: (value: MembershipsFilterValue) => void;

  /** Disable all controls (e.g. while loading). */
  disabled?: boolean;

  /** Show the inline Clear button. Turn off inside a FilterDrawer (it has Reset). */
  showClear?: boolean;
}

// ============================================
// Constants
// ============================================

export const EMPTY_MEMBERSHIPS_FILTER: MembershipsFilterValue = {
  membershipTypeId: null,
  validity: null,
  source: null,
  payments: DEFAULT_PAYMENTS_VIEW,
};

const isFilterActive = (value: MembershipsFilterValue): boolean =>
  Boolean(value.membershipTypeId || value.validity || value.source || value.payments !== DEFAULT_PAYMENTS_VIEW);

// Each control flexes to fill space and wraps to its own row when cramped. The
// segmented controls need more room (several segments) than the Type select.
const TYPE_STYLE: React.CSSProperties = { flex: "1 1 180px", minWidth: 160 };
const STATUS_STYLE: React.CSSProperties = { flex: "2 1 300px", minWidth: 260 };
const SOURCE_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

// The "no filter" segment value (SegmentedControl can't hold null).
const ANY = "any";

// ============================================
// Component
// ============================================

const MembershipsFilterBarComponent: React.FC<MembershipsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["memberships", "common"]);
  const { isMobile } = useAppEnvironment();
  const typesCtx = useOptionalMembershipTypesContext();

  const soonHint = t("memberships:filters.validity.soonHint", {
    defaultValue: "“Soon” = expiring within {{days}} days",
    days: MEMBERSHIP_EXPIRING_SOON_DAYS,
  });

  const typeOptions = useMemo(
    () =>
      (typesCtx?.membershipTypes ?? []).map((type) => ({
        value: type.id!,
        label: type.name ?? type.id!,
      })),
    [typesCtx?.membershipTypes]
  );

  // Segmented options — short labels so several segments fit. "Any" clears.
  const validityOptions = useMemo(
    () => [
      { value: ANY, label: t("memberships:filters.validity.any", { defaultValue: "Any" }) },
      {
        value: "active",
        label: t("memberships:filters.validity.active", { defaultValue: "Active" }),
      },
      {
        value: "expiring_soon",
        // Tooltip carries the "within N days" meaning on larger screens (where
        // the description is hidden to keep the row's labels aligned).
        label: (
          <Tooltip label={soonHint} withArrow position="top" openDelay={300}>
            <span>{t("memberships:filters.validity.soon", { defaultValue: "Soon" })}</span>
          </Tooltip>
        ),
      },
      {
        value: "expired",
        label: t("memberships:filters.validity.expired", { defaultValue: "Expired" }),
      },
    ],
    [t, soonHint]
  );

  const sourceOptions = useMemo(
    () => [
      { value: ANY, label: t("memberships:filters.source.any", { defaultValue: "Any" }) },
      {
        value: "internal",
        label: t("memberships:filters.source.internal", { defaultValue: "Internal" }),
      },
      {
        value: "external",
        label: t("memberships:filters.source.external", { defaultValue: "External" }),
      },
    ],
    [t]
  );

  const active = isFilterActive(value);

  const handleClear = () => {
    onChange(EMPTY_MEMBERSHIPS_FILTER);
  };

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={TYPE_STYLE}>
        <SelectField
          label={t("memberships:filters.type.label", { defaultValue: "Type" })}
          placeholder={t("memberships:filters.type.placeholder", {
            defaultValue: "All types",
          })}
          data={typeOptions}
          value={value.membershipTypeId}
          onChange={(_name, next) => onChange({ ...value, membershipTypeId: next })}
          disabled={disabled}
          clearable
          searchable
          comboboxProps={DRAWER_COMBOBOX_PROPS}
          size="md"
        />
      </Box>

      <Box style={STATUS_STYLE}>
        <Input.Wrapper
          label={t("memberships:filters.validity.label", { defaultValue: "Status" })}
          // On phones (stacked in the drawer) show the hint inline; on larger
          // screens it lives in the "Soon" tooltip so it doesn't offset the row.
          description={isMobile ? soonHint : undefined}
        >
          <SegmentedControl
            data={validityOptions}
            value={value.validity ?? ANY}
            onChange={(next) =>
              onChange({
                ...value,
                validity: next === ANY ? null : (next as MembershipValidity),
              })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={SOURCE_STYLE}>
        <Input.Wrapper
          label={t("memberships:filters.source.label", { defaultValue: "Source" })}
        >
          <SegmentedControl
            data={sourceOptions}
            value={value.source ?? ANY}
            onChange={(next) =>
              onChange({
                ...value,
                source: next === ANY ? null : (next as MembershipSource),
              })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={SOURCE_STYLE}>
        <Input.Wrapper label={t("memberships:filters.payments.label", { defaultValue: "Payments" })}>
          <Box mt={4}>
            <TestPaymentsControl value={value.payments} onChange={(payments) => onChange({ ...value, payments })} disabled={disabled} fullWidth />
          </Box>
        </Input.Wrapper>
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={handleClear}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const MembershipsFilterBar = React.memo(MembershipsFilterBarComponent);
